// Generated from backend/src/licensing/license-relevance.ts. Run sync-license-relevance.cjs.
// Deliberately empty: informational credentialing copy is not authority data.
// Add reviewed, dated, sourced rules here through the adapter boundary.
export const COVERAGE_RULES = [];
export const LICENSE_POLICY_VERSION = 'license-relevance-v2';
export const LICENSE_LABELS = { RN: 'RN', APRN: 'APRN', MEDICAL: 'medical' };
export function applicableLicenseKinds(profession, isStudent = false) {
    if (isStudent)
        return [];
    if (profession === 'CRNA')
        return ['RN', 'APRN'];
    if (profession === 'Anesthesiologist')
        return ['MEDICAL'];
    return [];
}
function sourced(refs) {
    return (Array.isArray(refs) &&
        refs.length > 0 &&
        refs.every((r) => !!r &&
            !!r.sourceUrl &&
            !!r.sourceRecordId &&
            !!r.parserVersion &&
            Number.isFinite(Date.parse(r.retrievedAt))));
}
function current(from, through, at) {
    const t = Date.parse(at);
    return (Number.isFinite(t) && Date.parse(from) <= t && t <= Date.parse(through));
}
/** Input is already normalized to a two-letter state by the host adapter. */
export function evaluateLicenseRelevance({ provider = {}, profession, state, requirements, requirementsState, at = new Date().toISOString(), rules = COVERAGE_RULES, }) {
    provider = provider && typeof provider === 'object' ? provider : {};
    const kinds = provider.providerType && provider.providerType !== profession
        ? []
        : applicableLicenseKinds(profession, provider.isSRNA);
    const target = /^[A-Z]{2}$/.test(state || '') ? state : undefined;
    const authorities = Array.isArray(provider.licenseAuthorities)
        ? provider.licenseAuthorities.filter((a) => a && typeof a === 'object')
        : [];
    const intents = Array.isArray(provider.licenseAcquisitionPreferences)
        ? provider.licenseAcquisitionPreferences.filter((i) => i && typeof i === 'object')
        : [];
    const coverageRules = Array.isArray(rules) ? rules : [];
    const requirementStateUnknown = typeof requirementsState !== 'string' ||
        !/^[A-Z]{2}$/.test(requirementsState);
    const requirementStateChanged = !requirementStateUnknown && requirementsState !== target;
    const details = kinds.map((licenseType) => {
        const requirement = requirementStateUnknown || requirementStateChanged
            ? 'unknown'
            : requirements?.[licenseType] || 'unknown';
        const base = {
            licenseType,
            requirement,
            state: target,
            authorityId: undefined,
            ruleId: undefined,
            linkMethod: undefined,
        };
        if (requirement === 'not_required')
            return {
                ...base,
                status: 'not_required',
                reasonCode: 'LICENSE_NOT_REQUIRED',
                askWillingness: false,
            };
        if (requirement !== 'required' || !target)
            return {
                ...base,
                status: 'unknown',
                reasonCode: !target
                    ? 'TARGET_STATE_UNKNOWN'
                    : requirementStateUnknown
                        ? 'REQUIREMENT_STATE_UNKNOWN'
                        : requirementStateChanged
                            ? 'REQUIREMENT_STATE_CHANGED'
                            : 'REQUIREMENT_UNKNOWN',
                askWillingness: false,
            };
        const evidence = authorities.filter((a) => a.licenseType === licenseType);
        const usable = (a) => a.status === 'active' &&
            a.linkMethod === 'deterministic' &&
            sourced(a.sourceRefs) &&
            current(a.validFrom, a.validThrough, at);
        const direct = evidence.find((a) => a.kind === 'state' && a.state === target && usable(a));
        if (direct)
            return {
                ...base,
                status: 'covered',
                reasonCode: 'ACTIVE_STATE_AUTHORITY',
                authorityId: direct.id,
                linkMethod: 'deterministic',
                askWillingness: false,
            };
        for (const authority of evidence.filter((a) => a.kind === 'compact' && usable(a))) {
            const rule = coverageRules.find((r) => r &&
                r.id === authority.ruleId &&
                r.licenseType === licenseType &&
                Array.isArray(r.homeStates) &&
                Array.isArray(r.targetStates) &&
                r.homeStates.includes(authority.state) &&
                r.targetStates.includes(target) &&
                sourced(r.sourceRefs) &&
                current(r.validFrom, r.validThrough, at));
            if (rule)
                return {
                    ...base,
                    status: 'covered',
                    reasonCode: 'REVIEWED_COMPACT_AUTHORITY',
                    authorityId: authority.id,
                    ruleId: rule.id,
                    linkMethod: 'deterministic',
                    askWillingness: false,
                };
        }
        // A state-only profile list is self-report/NPI enrichment, not active-status
        // verification. Conflicting observed state evidence blocks the listed badge.
        const field = {
            RN: 'stateLicenses',
            APRN: 'stateAPRNLicenses',
            MEDICAL: 'stateMedicalLicenses',
        }[licenseType];
        const listed = Array.isArray(provider[field]) ? provider[field] : [];
        const conflict = evidence.some((a) => a.kind === 'state' && a.state === target);
        if (!conflict && listed.includes(target)) {
            const profileSource = provider.dataProvenance?.[field]?.source || 'unknown';
            return {
                ...base,
                status: 'listed',
                reasonCode: 'PROFILE_LICENSE_LISTED',
                profileSource,
                linkMethod: ['user_submitted', 'user_verified'].includes(profileSource)
                    ? 'self_reported'
                    : undefined,
                askWillingness: false,
            };
        }
        const intent = intents.find((i) => i.state === target && i.licenseType === licenseType)?.response;
        const uncertain = evidence.some((a) => a.kind === 'compact' ||
            (a.kind === 'state' && a.state === target && !usable(a)));
        return {
            ...base,
            status: intent === 'yes'
                ? 'willing'
                : intent === 'no'
                    ? 'unwilling'
                    : 'unknown',
            reasonCode: intent === 'yes'
                ? 'WILLING_TO_OBTAIN'
                : intent === 'no'
                    ? 'NOT_WILLING_TO_OBTAIN'
                    : uncertain
                        ? 'AUTHORITY_NEEDS_CONFIRMATION'
                        : 'LICENSE_NOT_CONFIRMED',
            linkMethod: intent === 'yes' || intent === 'no' ? 'self_reported' : undefined,
            askWillingness: true,
        };
    });
    const required = details.filter((d) => d.requirement === 'required');
    const unknownRequirement = details.some((d) => d.requirement === 'unknown');
    const allCovered = required.length > 0 && required.every((d) => d.status === 'covered');
    const allListedOrCovered = required.length > 0 &&
        required.every((d) => ['covered', 'listed'].includes(d.status));
    const status = !kinds.length
        ? 'inapplicable'
        : !target || unknownRequirement
            ? 'unknown'
            : !required.length
                ? 'not_required'
                : allCovered
                    ? 'covered'
                    : allListedOrCovered
                        ? 'listed'
                        : required.some((d) => d.status === 'unwilling')
                            ? 'unwilling'
                            : required.every((d) => ['covered', 'listed', 'willing'].includes(d.status))
                                ? 'willing'
                                : 'unknown';
    // A bounded relevance nudge, never a hard eligibility filter. Unknown and
    // not-required are neutral; intent never masquerades as authority.
    const rankingBoost = status === 'covered'
        ? 15
        : status === 'listed'
            ? 8
            : status === 'willing'
                ? 3
                : 0;
    return {
        policyVersion: LICENSE_POLICY_VERSION,
        evaluatedAt: at,
        state: target,
        status,
        rankingBoost,
        details,
        clearance: 'not_assessed',
    };
}

